import { Flex, Group, Title } from "@mantine/core";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { IconCreditCard, IconDownload, IconUpload } from "@tabler/icons-react";
import { useMemo, useState } from "react";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { buildImportColumns } from "@/components/features/AccountsPayable/SpPaymentMethods/BulkUpload/spPaymentMethodImportColumns";
import { buildSpPaymentMethodImportDescriptor } from "@/components/features/AccountsPayable/SpPaymentMethods/BulkUpload/spPaymentMethodImportDescriptor";
import { exportSpPaymentMethodImportFile } from "@/components/features/AccountsPayable/SpPaymentMethods/BulkUpload/exportSpPaymentMethodImportFile";
import { SpPaymentMethodList } from "@/components/features/AccountsPayable/SpPaymentMethods/SpPaymentMethodList";
import { Button } from "@/components/ui/Button/Button";
import BulkImportModal from "@/components/ui/BulkImport/BulkImportModal";
import { useGetApConfig, useGetPaymentFields } from "@/hooks/accounts-payable/useApManagement";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { handleApiSuccess, showErrorAlert } from "@/utils/responseHandler";

import styles from "./sp-payment-methods.module.scss";

const EXPORT_FAILED_MESSAGE = "The export could not be created. Try again.";

export const Route = createFileRoute("/_authenticated/accounts-payable/sp-payment-methods")({
  component: SpPaymentMethods,
  validateSearch: tableSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function SpPaymentMethods() {
  const searchParams = useSearch({ from: "/_authenticated/accounts-payable/sp-payment-methods" });
  const { hasAccess } = usePermissions();
  const canImport = hasAccess({ module: MODULE_KEYS_MAP.accountsPayable }).create;
  const { data: registry } = useGetPaymentFields(canImport);
  const columns = useMemo(() => (registry ? buildImportColumns(registry) : null), [registry]);
  const { data: methodDefs } = useGetApConfig("payment-method-def", { enabled: canImport });
  const descriptor = useMemo(
    () =>
      columns && methodDefs ? buildSpPaymentMethodImportDescriptor(columns, methodDefs) : null,
    [columns, methodDefs],
  );
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!columns) return;
    setIsExporting(true);
    try {
      const count = await exportSpPaymentMethodImportFile(columns);
      handleApiSuccess(`Exported ${count} service providers.`);
    } catch {
      showErrorAlert(EXPORT_FAILED_MESSAGE);
    } finally {
      setIsExporting(false);
    }
  };

  useBreadcrumb(
    [
      {
        title: "SP Payment Methods",
        href: "/accounts-payable/sp-payment-methods",
        icon: <IconCreditCard size={20} stroke={1.5} />,
      },
    ],
    { listView: "List View" },
  );

  return (
    <Flex direction="column" className={styles.container}>
      <Group justify="space-between" align="center" className={styles.header}>
        <Title order={3} className={styles.title}>
          SP Payment Methods
        </Title>
        {canImport && (
          <Group gap="var(--spacing-small)">
            <Button
              variant="secondary"
              size="small"
              iconLeft={<IconDownload size={16} />}
              isDisabled={!columns || isExporting}
              onClick={() => void handleExport()}
            >
              {isExporting ? "Exporting..." : "Export"}
            </Button>
            <Button
              variant="primary"
              size="small"
              iconLeft={<IconUpload size={16} />}
              isDisabled={!descriptor}
              onClick={() => setIsImportOpen(true)}
            >
              Bulk Upload
            </Button>
          </Group>
        )}
      </Group>

      <SpPaymentMethodList searchParams={searchParams} />

      {descriptor && (
        <BulkImportModal
          descriptor={descriptor}
          ctx={undefined}
          opened={isImportOpen}
          onClose={() => setIsImportOpen(false)}
        />
      )}
    </Flex>
  );
}
